import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const LocationContext = createContext();

// Default values for location settings
const defaultSettings = {
  zipCode: '',
  radius: 50, // Default 50 miles
  userCoordinates: null, // { lat, lng }
};

// Provider component
export const LocationProvider = ({ children }) => {
  // Initialize state from localStorage or use defaults
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('location-settings');
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });

  // Update localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem('location-settings', JSON.stringify(settings));
  }, [settings]);

  // Set zip code
  const setZipCode = (zipCode) => {
    setSettings(prev => ({
      ...prev,
      zipCode: zipCode.trim(),
    }));
  };

  // Set radius
  const setRadius = (radius) => {
    setSettings(prev => ({
      ...prev,
      radius: parseInt(radius, 10),
    }));
  };

  // Set user coordinates (from geolocation or zip code lookup)
  const setUserCoordinates = (coordinates) => {
    setSettings(prev => ({
      ...prev,
      userCoordinates: coordinates,
    }));
  };

  // Clear all location settings
  const clearLocation = () => {
    setSettings(defaultSettings);
  };

  // Request geolocation from browser
  const requestGeolocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserCoordinates(coordinates);
          resolve(coordinates);
        },
        (error) => {
          reject(error);
        }
      );
    });
  };

  // Context value
  const value = {
    ...settings,
    setZipCode,
    setRadius,
    setUserCoordinates,
    clearLocation,
    requestGeolocation,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook for using the location context
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;
